import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { TimePeriod } from "../types/TimePeriod";
import { useDebounce } from "../customHooks/useDebounce";

interface TimePeriodContextType {
  periods: TimePeriod[];
  selectedIndex: number;
  selectedPeriod: TimePeriod;
  setSelectedIndex: (index: number) => void;
  setDirty: (dirty: boolean) => void;
  hasPendingChange: boolean;
  confirmPending: () => void;
  cancelPending: () => void;
}

const TimePeriodContext = createContext<TimePeriodContextType | undefined>(undefined);

export const TimePeriodProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const periods: TimePeriod[] = useMemo(() => {
    const now = new Date();
    return [-2, -1, 0, 1].map((offset) => {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return {
        Month: d.getMonth() + 1,
        Year: d.getFullYear(),
      };
    });
  }, []);

  const [selectedIndex, setSelectedIndexRaw] = useState(1);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const dirtyRef = useRef(false);
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;

  const setDirty = useCallback((d: boolean) => {
    dirtyRef.current = d;
  }, []);

  const setSelectedIndex = useCallback((i: number) => {
    if (i === selectedIndexRef.current) return;
    if (dirtyRef.current) setPendingIndex(i);
    else setSelectedIndexRaw(i);
  }, []);

  const confirmPending = useCallback(() => {
    if (pendingIndex !== null) setSelectedIndexRaw(pendingIndex);
    dirtyRef.current = false;
    setPendingIndex(null);
  }, [pendingIndex]);

  const cancelPending = useCallback(() => setPendingIndex(null), []);

  const selectedPeriod = useDebounce(
    useMemo(() => periods[selectedIndex], [periods, selectedIndex]),
    300,
  );

  return (
    <TimePeriodContext.Provider
      value={{
        periods,
        selectedIndex,
        selectedPeriod,
        setSelectedIndex,
        setDirty,
        hasPendingChange: pendingIndex !== null,
        confirmPending,
        cancelPending,
      }}
    >
      {children}
    </TimePeriodContext.Provider>
  );
};

export const useTimePeriodContext = () => {
  const context = useContext(TimePeriodContext);
  if (!context) throw new Error("useTimePeriodContext must be used within a TimePeriodProvider");
  return context;
};
